import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";

const inputClassName =
  "w-full wireframe-border bg-whart-bg px-3 py-2 text-sm text-whart-text outline-none focus:border-whart-dark disabled:opacity-50";

interface ArticleSuggestionSearchFieldProps<T> {
  label: string;
  placeholder: string;
  searchingLabel: string;
  selectHintLabel: string;
  noResultsLabel: string;
  disabled: boolean;
  onSearch: (query: string) => Promise<{
    success: boolean;
    error?: string;
    items: T[];
  }>;
  onSelect: (item: T) => Promise<{ success: boolean; error?: string }>;
  getItemId: (item: T) => string;
  renderPrimary: (item: T) => string;
  renderSecondary?: (item: T) => string | null;
}

export function ArticleSuggestionSearchField<T>({
  label,
  placeholder,
  searchingLabel,
  selectHintLabel,
  noResultsLabel,
  disabled,
  onSearch,
  onSelect,
  getItemId,
  renderPrimary,
  renderSecondary,
}: ArticleSuggestionSearchFieldProps<T>) {
  const listboxId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<T[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setItems([]);
      setIsSearching(false);
      setSearchError(null);
      return;
    }

    setIsSearching(true);
    const timer = window.setTimeout(async () => {
      const result = await onSearch(trimmed);
      setIsSearching(false);

      if (!result.success) {
        setSearchError(result.error ?? null);
        setItems([]);
        setIsOpen(false);
        return;
      }

      setSearchError(null);
      setItems(result.items);
      setIsOpen(result.items.length > 0);
      setActiveIndex(-1);
    }, 300);

    return () => window.clearTimeout(timer);
  }, [query, onSearch]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  async function handleSelect(item: T) {
    setIsSelecting(true);
    setSelectionError(null);

    const result = await onSelect(item);
    setIsSelecting(false);

    if (!result.success) {
      setSelectionError(result.error ?? null);
      return;
    }

    setQuery("");
    setItems([]);
    setIsOpen(false);
    setActiveIndex(-1);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (!isOpen || items.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((current) =>
        current < items.length - 1 ? current + 1 : 0,
      );
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((current) =>
        current > 0 ? current - 1 : items.length - 1,
      );
      return;
    }

    if (event.key === "Enter" && activeIndex >= 0) {
      event.preventDefault();
      const item = items[activeIndex];
      if (item) {
        void handleSelect(item);
      }
      return;
    }

    if (event.key === "Escape") {
      setIsOpen(false);
      setActiveIndex(-1);
    }
  }

  const showDropdown = isOpen && items.length > 0 && !isSelecting;
  const isBusy = disabled || isSelecting;

  return (
    <section className="relative space-y-2" ref={containerRef}>
      <label
        htmlFor={`${listboxId}-search`}
        className="text-xs font-medium uppercase tracking-wider text-neutral-500"
      >
        {label}
      </label>
      <input
        id={`${listboxId}-search`}
        type="search"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setIsOpen(true);
        }}
        onFocus={() => {
          if (items.length > 0) setIsOpen(true);
        }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={isBusy}
        autoComplete="off"
        role="combobox"
        aria-expanded={showDropdown}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={
          activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined
        }
        className={inputClassName}
      />
      {isSearching || isSelecting ? (
        <p className="text-xs text-neutral-500">{searchingLabel}</p>
      ) : null}
      {!isSearching && query.trim().length > 0 && query.trim().length < 2 ? (
        <p className="text-xs text-neutral-500">{selectHintLabel}</p>
      ) : null}
      {!isSearching &&
      query.trim().length >= 2 &&
      items.length === 0 &&
      !searchError ? (
        <p className="text-xs text-neutral-500">{noResultsLabel}</p>
      ) : null}
      {searchError ? (
        <p role="alert" className="text-xs text-whart-urgent">
          {searchError}
        </p>
      ) : null}
      {selectionError ? (
        <p role="alert" className="text-xs text-whart-urgent">
          {selectionError}
        </p>
      ) : null}
      {showDropdown ? (
        <ul
          id={listboxId}
          role="listbox"
          className="absolute z-20 mt-1 max-h-60 w-full overflow-y-auto border border-black/10 bg-white shadow-sm"
        >
          {items.map((item, index) => {
            const secondary = renderSecondary?.(item);

            return (
              <li key={getItemId(item)} role="presentation">
                <button
                  id={`${listboxId}-option-${index}`}
                  type="button"
                  role="option"
                  aria-selected={index === activeIndex}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => void handleSelect(item)}
                  className={`w-full px-3 py-2 text-left text-sm transition-colors hover:bg-neutral-50 ${
                    index === activeIndex ? "bg-neutral-50" : ""
                  }`}
                >
                  <span className="block font-medium text-whart-text">
                    {renderPrimary(item)}
                  </span>
                  {secondary ? (
                    <span className="block text-xs text-neutral-500">
                      {secondary}
                    </span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </section>
  );
}
